import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Appointments from './pages/Appointments';
import Employees from './pages/Employees';
import Clients from './pages/Clients';
import Revenue from './pages/Revenue';
import ServiceMenu from './pages/ServiceMenu';
import Analytics from './pages/Analytics';

// Public site
import PublicLayout from './components/public/PublicLayout';
import Home from './pages/public/Home';
import Plans from './pages/public/Plans';
import Services from './pages/public/Services';
import HowItWorks from './pages/public/HowItWorks';
import About from './pages/public/About';
import BookNow from './pages/public/BookNow';
import NailCash from './pages/public/NailCash';
import Gallery from './pages/public/Gallery';

import './App.css';

// Wrapper for portal (auth required)
const PortalShell = ({ theme, toggleTheme, currentUser, onLogout, sidebarCollapsed, setSidebarCollapsed, searchQuery, setSearchQuery, children }) => {
  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-container">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          theme={theme}
          onThemeToggle={toggleTheme}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentUser={currentUser}
          onLogout={onLogout}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-[1440px] mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

const RequireAuth = ({ currentUser, children }) => {
  const location = useLocation();
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

const LoginPage = ({ onLogin, currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (currentUser) {
      const dest = location.state?.from?.pathname || '/portal';
      navigate(dest, { replace: true });
    }
  }, [currentUser, navigate, location.state]);
  return <Auth onLogin={onLogin} />;
};

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setSidebarCollapsed(true);
      else setSidebarCollapsed(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');
  const handleLogin = (user) => setCurrentUser(user);
  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
  };

  const publicWrap = (Page) => (
    <PublicLayout>
      <Page />
    </PublicLayout>
  );

  const portalWrap = (Component) => (
    <RequireAuth currentUser={currentUser}>
      <PortalShell
        theme={theme}
        toggleTheme={toggleTheme}
        currentUser={currentUser}
        onLogout={handleLogout}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      >
        <Component />
      </PortalShell>
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing site */}
        <Route path="/" element={publicWrap(Home)} />
        <Route path="/plans" element={publicWrap(Plans)} />
        <Route path="/services" element={publicWrap(Services)} />
        <Route path="/how-it-works" element={publicWrap(HowItWorks)} />
        <Route path="/about" element={publicWrap(About)} />
        <Route path="/book" element={publicWrap(BookNow)} />
        <Route path="/nail-cash" element={publicWrap(NailCash)} />
        <Route path="/gallery" element={publicWrap(Gallery)} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage onLogin={handleLogin} currentUser={currentUser} />} />

        {/* Private admin portal */}
        <Route path="/portal" element={portalWrap(Dashboard)} />
        <Route path="/portal/appointments" element={portalWrap(Appointments)} />
        <Route path="/portal/employees" element={portalWrap(Employees)} />
        <Route path="/portal/clients" element={portalWrap(Clients)} />
        <Route path="/portal/revenue" element={portalWrap(Revenue)} />
        <Route path="/portal/services" element={portalWrap(ServiceMenu)} />
        <Route path="/portal/analytics" element={portalWrap(Analytics)} />

        {/* Backward-compat: old portal URLs */}
        <Route path="/appointments" element={<Navigate to="/portal/appointments" replace />} />
        <Route path="/employees" element={<Navigate to="/portal/employees" replace />} />
        <Route path="/clients" element={<Navigate to="/portal/clients" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
